import React, { useMemo, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonButton,
  IonCheckbox,
  IonIcon,
  IonSkeletonText,
  isPlatform,
  useIonViewWillLeave,
} from '@ionic/react';
import { shareOutline, shareSocialOutline } from 'ionicons/icons';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { ContactLogic } from '@/modules/contact';
import type { MessageIntent } from '@/modules/template';
import SectionCard from '@/components/SectionCard';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import ShareWizardHeader from './ShareWizardHeader';
import { Destinations } from './navigation_logic';
import RecipientBox from './RecipientBox';
import { INTENT_SYMBOL, ShareWizardLogic } from './logic';
import { useImpliedConfirm } from './useImpliedConfirm';
import { useRecipientConfirm } from './useRecipientConfirm';
import { MePrFoService } from '@/services/meprfo';
import { pxToRem } from '@/modules/config/constants';

const INTENT_PREFIX: Record<MessageIntent, string> = {
  intro: 'Intro',
  reminder: 'Remind',
  update: 'Update',
};

function IntentLabel({ intent }: { intent: MessageIntent }) {
  return (
    <>
      {INTENT_PREFIX[intent]} {INTENT_SYMBOL[intent]}
    </>
  );
}

const ShareConfirmStep: React.FC = () => {
  const {
    topic,
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    impliedConfirm,
    setImpliedConfirm,
    isLoadingLegState,
    handleOpenFirstShare,
    handleConfirmAndOpenNext,
    handleRetryCurrentShare,
    setShowInterruptWarning,
    nav,
  } = useShareWizardContext();

  const [swearToReturn, setSwearToReturn] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const { isIPad, isMacOS } = useDeviceInfo();
  // On a desktop browser or iPad there's no "phone" to jump to, so refer to the
  // generic "device's" messaging app instead.
  const deviceWord =
    isIPad || isMacOS || isPlatform('desktop') ? 'device' : 'phone';

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // On the final share, an implied confirm advances forward to final-share-confirm.
  useImpliedConfirm({ navigateToFinalShareConfirm: true });

  const totalShares = sendQueue.length;
  const currentShare = sendQueue[sendQueueCursor] ?? null;
  const nextShare = sendQueue[sendQueueCursor + 1] ?? null;

  // Variants of the share-confirm step:
  //   first-share     — initial agree-to-return screen for the first sharing action
  //   next-share      — confirm previous sharing action; preview & open the next one
  //   revisited-share — user wants to retry the sharing they just performed.
  //                     Fires either after backing out of FinalShareConfirmStep
  //                     (final share) or after clicking Oops on next-share.
  const isFirstShare = sendQueueCursor === 0 && inProgressLegId === null;
  const isNextShare =
    !isRetrying &&
    inProgressLegId !== null &&
    sendQueueCursor < totalShares - 1;
  // Also covers the recovery case: cursor advanced but inProgressLegId is null
  // (page reloaded between setInProgressLegId(null) committing and the
  // initiateSharingLeg result being persisted). handleRetryCurrentShare will
  // create a fresh leg when existingInProgressId is undefined.
  const isRevisitedShare = totalShares > 0 && !isFirstShare && !isNextShare;

  // A recipient can tap the leg link to confirm receipt before the sender
  // returns to confirm. Poll the just-sent leg (only on the next-share variant,
  // and only until the sender's own implied-confirm beats them to it) so the
  // status reflects a recipient-confirm instead of staying stale at "unconfirmed".
  const recipientConfirmed = useRecipientConfirm(
    inProgressLegId,
    isNextShare && !impliedConfirm
  );

  const previewedShare = isFirstShare
    ? (sendQueue[0] ?? null)
    : isNextShare
      ? nextShare
      : isRevisitedShare
        ? currentShare
        : null;

  const sortByLastName = useMemo(
    () =>
      ContactLogic.lastNameComparator([
        ...(previewedShare?.header.recipients ?? []),
      ]),
    [previewedShare]
  );

  // On a cold load (fresh tab / killed webview) persisted state hasn't restored
  // yet, so sendQueue is empty. Show a skeleton until hydration completes rather
  // than flashing the "Nothing to confirm yet" empty state. Placed after all
  // hooks so hook order stays stable.
  if (isLoadingLegState) {
    return (
      <IonPage>
        <ShareWizardHeader topicTitle="" progress={0} />
        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '16px 20px',
              gap: '16px',
            }}
          >
            <IonSkeletonText
              animated
              style={{ height: '88px', borderRadius: '12px' }}
            />
            <IonSkeletonText
              animated
              style={{ height: '72px', borderRadius: '12px' }}
            />
            <IonSkeletonText
              animated
              style={{ height: '48px', borderRadius: '8px', marginTop: '8px' }}
            />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const previewedShareIndex = isFirstShare
    ? 1
    : isNextShare
      ? sendQueueCursor + 2
      : isRevisitedShare
        ? sendQueueCursor + 1
        : 0;

  const baseTitle = topic?.title ?? '';
  const progressLabel =
    totalShares > 1
      ? `Message ${previewedShareIndex} of ${totalShares}`
      : undefined;

  const shareLabel = 'Share in messaging app';

  if (totalShares === 0) {
    return (
      <IonPage>
        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <p style={{ color: '#888' }}>
              Nothing to confirm yet. Pick recipients, a tone, and compose your
              message first.
            </p>
            <IonButton fill="clear" onClick={nav.exit}>
              Close
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const handleOpen = async () => {
    setIsConfirming(true);
    try {
      if (isFirstShare) {
        await handleOpenFirstShare();
      } else {
        await handleConfirmAndOpenNext();
      }
    } finally {
      setIsConfirming(false);
    }
  };

  // Variant: first-share
  if (isFirstShare) {
    const leg = sendQueue[0];
    const intent = leg?.header.intent;

    return (
      <IonPage>
        <ShareWizardHeader
          topicTitle={baseTitle}
          title={progressLabel}
          progress={nav.progress}
          onBack={() => setShowInterruptWarning(true)}
        />
        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              // Bottom clearance so the content clears the fixed FooterDock.
              padding: '16px 20px 120px',
              gap: '20px',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              {leg && leg.header.recipients.length > 0 && (
                <RecipientBox
                  to={intent ? <IntentLabel intent={intent} /> : ''}
                  value={leg.header.recipients.map(
                    (contact: { localId: string }) => contact.localId
                  )}
                  sort={sortByLastName}
                  readOnly
                  collapsedRows={3}
                  expandable={false}
                />
              )}

              {leg?.body && (
                <SectionCard
                  className="section-card--message-preview"
                  noBottomMargin
                >
                  <div className="section-card__message-preview">
                    {leg.body.length > 75
                      ? `${leg.body.slice(0, 75).trimEnd()}…`
                      : leg.body}
                  </div>
                </SectionCard>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '12px',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: pxToRem(14),
                  lineHeight: 1.4,
                  color: '#666',
                }}
              >
                Here&apos;s how sharing works: the button below opens your{' '}
                {deviceWord}&apos;s messaging app with your message ready to
                send. After you send it there, come back here to
                confirm—it&apos;s what makes your sends count and it keeps
                anyone from getting a double text.
              </p>

              <IonCheckbox
                className="ion-text-wrap"
                checked={swearToReturn}
                onIonChange={e => setSwearToReturn(e.detail.checked)}
                labelPlacement="end"
                style={{ fontSize: pxToRem(15) }}
              >
                I agree to come back &amp; confirm after sending
              </IonCheckbox>
            </div>
          </div>
        </IonContent>
        <FooterDock background="transparent">
          <FooterDockRow landmark>
            <SmartCaseButton
              expand="block"
              color="primary"
              onClick={handleOpen}
              disabled={!swearToReturn || isConfirming}
              style={{ margin: 0 }}
            >
              <IonIcon
                slot="start"
                ios={shareOutline}
                md={shareSocialOutline}
              />
              {shareLabel}
            </SmartCaseButton>
          </FooterDockRow>
        </FooterDock>
      </IonPage>
    );
  }

  // Variant: next-share
  if (isNextShare) {
    const sentCount = currentShare?.header.recipients.length ?? 0;
    const nextIntent = nextShare?.header.intent;
    const confirmed = impliedConfirm || recipientConfirmed;
    // Hide Retry once the sole recipient has self-confirmed: re-sending would
    // just spam someone who already received the message.
    const hideRetry = recipientConfirmed && sentCount === 1;

    const soleRecipientName = currentShare
      ? ShareWizardLogic.soleRecipientName(currentShare.header.recipients)
      : '';
    const sentToLabel = ShareWizardLogic.sentToLabel(
      sentCount,
      soleRecipientName
    );

    return (
      <IonPage>
        <ShareWizardHeader
          topicTitle={baseTitle}
          title={progressLabel}
          progress={nav.progress}
          onBack={() => setIsRetrying(true)}
        />
        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              // Bottom clearance so the content clears the fixed FooterDock.
              padding: '16px 20px 120px',
              gap: '20px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: pxToRem(15),
                  color: confirmed ? 'var(--ion-color-success)' : '#ff9800',
                  fontWeight: 600,
                }}
              >
                {sentToLabel} —{' '}
                {recipientConfirmed
                  ? 'recipient-confirmed ✓'
                  : impliedConfirm
                    ? 'confirmed ✓'
                    : 'unconfirmed'}
              </p>
              {!hideRetry && (
                <IonButton
                  size="small"
                  fill="outline"
                  onClick={async () => {
                    if (impliedConfirm && inProgressLegId) {
                      await MePrFoService.markLegAsInProgress(inProgressLegId);
                      setImpliedConfirm(false);
                    }
                    setIsRetrying(true);
                  }}
                  disabled={isConfirming}
                >
                  Retry
                </IonButton>
              )}
            </div>

            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              {nextShare && nextShare.header.recipients.length > 0 && (
                <RecipientBox
                  to={nextIntent ? <IntentLabel intent={nextIntent} /> : ''}
                  value={nextShare.header.recipients.map(
                    (contact: { localId: string }) => contact.localId
                  )}
                  sort={sortByLastName}
                  readOnly
                  collapsedRows={3}
                  expandable={false}
                />
              )}

              {nextShare?.body && (
                <SectionCard
                  className="section-card--message-preview"
                  noBottomMargin
                >
                  <div className="section-card__message-preview">
                    {nextShare.body.length > 75
                      ? `${nextShare.body.slice(0, 75).trimEnd()}…`
                      : nextShare.body}
                  </div>
                </SectionCard>
              )}
            </div>
          </div>
        </IonContent>
        <FooterDock background="transparent">
          <FooterDockRow landmark>
            <SmartCaseButton
              expand="block"
              color="primary"
              onClick={handleOpen}
              disabled={isConfirming}
              style={{ margin: 0 }}
            >
              <IonIcon
                slot="start"
                ios={shareOutline}
                md={shareSocialOutline}
              />
              {confirmed
                ? 'Share next message'
                : 'Confirm previous & share next'}
            </SmartCaseButton>
          </FooterDockRow>
        </FooterDock>
      </IonPage>
    );
  }

  // Variant: revisited-share
  if (isRevisitedShare) {
    const leg = currentShare;
    const intent = leg?.header.intent;

    const isLastBatch = sendQueueCursor === totalShares - 1;

    const handleReopen = async () => {
      setIsConfirming(true);
      try {
        handleRetryCurrentShare();
        if (isLastBatch) {
          nav.goTo(Destinations.sent, 'forward');
        } else {
          setIsRetrying(false);
        }
      } finally {
        setIsConfirming(false);
      }
    };

    return (
      <IonPage>
        <ShareWizardHeader
          topicTitle={baseTitle}
          title={progressLabel}
          progress={nav.progress}
          // Back is monotonic here: from the next-share preview, back drops to
          // this revisited view of the sharing just performed; backing out again must
          // continue *backward* (interrupt the send), not flip forward to the
          // next-share preview. Toggling isRetrying off did the latter, which
          // ping-ponged next-share ⇄ revisited-share in an endless loop.
          onBack={() => setShowInterruptWarning(true)}
        />
        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              // Bottom clearance so the content clears the fixed FooterDock.
              padding: '16px 20px 120px',
              gap: '20px',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              {leg && leg.header.recipients.length > 0 && (
                <RecipientBox
                  to={intent ? <IntentLabel intent={intent} /> : ''}
                  value={leg.header.recipients.map(
                    (contact: { localId: string }) => contact.localId
                  )}
                  sort={sortByLastName}
                  readOnly
                  collapsedRows={3}
                  expandable={false}
                />
              )}

              {leg?.body && (
                <SectionCard
                  className="section-card--message-preview"
                  noBottomMargin
                >
                  <div className="section-card__message-preview">
                    {leg.body.length > 75
                      ? `${leg.body.slice(0, 75).trimEnd()}…`
                      : leg.body}
                  </div>
                </SectionCard>
              )}
            </div>
          </div>
        </IonContent>
        <FooterDock background="transparent">
          <FooterDockRow landmark>
            <SmartCaseButton
              expand="block"
              color="primary"
              onClick={handleReopen}
              disabled={isConfirming}
              style={{ margin: 0 }}
            >
              <IonIcon
                slot="start"
                ios={shareOutline}
                md={shareSocialOutline}
              />
              {shareLabel}
            </SmartCaseButton>
          </FooterDockRow>
        </FooterDock>
      </IonPage>
    );
  }

  return null;
};

export default ShareConfirmStep;
